/**
 * 选择登录方式步骤
 * 固定高度，顺滑切换
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Smartphone, Lock } from 'lucide-react';
import WechatQRCode from '../WechatQRCode';
import PhoneLoginStep from './PhoneLoginStep';
import PasswordLoginStep from './PasswordLoginStep';

// 微信图标 SVG
const WechatIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor">
        <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.045c.134 0 .24-.111.24-.246 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088-.104-.005-.208-.035-.406-.035zm-2.092 3.091c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.827 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982z" />
    </svg>
);

// 登录方式枚举
const LOGIN_METHODS = {
    WECHAT: 'wechat',
    PHONE: 'phone',
    PASSWORD: 'password',
};

const SelectMethodStep = ({
    onWechatSuccess,
    onWechatNeedBindPhone,
    onLoginSuccess,
    onLoginError,
}) => {
    const [activeMethod, setActiveMethod] = useState(LOGIN_METHODS.WECHAT);

    // 渲染当前登录方式内容
    const renderContent = () => {
        switch (activeMethod) {
            case LOGIN_METHODS.WECHAT:
                return (
                    <WechatQRCode
                        onSuccess={onWechatSuccess}
                        onNeedBindPhone={onWechatNeedBindPhone}
                    />
                );
            case LOGIN_METHODS.PHONE:
                return (
                    <PhoneLoginStep
                        onSuccess={onLoginSuccess}
                        onError={onLoginError}
                    />
                );
            case LOGIN_METHODS.PASSWORD:
                return (
                    <PasswordLoginStep
                        onSuccess={onLoginSuccess}
                        onError={onLoginError}
                    />
                );
            default:
                return null;
        }
    };

    // 底部切换配置
    const getOtherMethods = () => {
        switch (activeMethod) {
            case LOGIN_METHODS.WECHAT:
                return [
                    { key: LOGIN_METHODS.PHONE, icon: Smartphone, label: '手机号' },
                    { key: LOGIN_METHODS.PASSWORD, icon: Lock, label: '密码' },
                ];
            case LOGIN_METHODS.PHONE:
                return [
                    { key: LOGIN_METHODS.WECHAT, icon: WechatIcon, label: '微信' },
                    { key: LOGIN_METHODS.PASSWORD, icon: Lock, label: '密码' },
                ];
            case LOGIN_METHODS.PASSWORD:
                return [
                    { key: LOGIN_METHODS.WECHAT, icon: WechatIcon, label: '微信' },
                    { key: LOGIN_METHODS.PHONE, icon: Smartphone, label: '手机号' },
                ];
            default:
                return [];
        }
    };

    return (
        <div className="flex flex-col">
            {/* 主内容区域 - 固定高度防止抖动 */}
            <div className="h-[380px] overflow-hidden">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeMethod}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="h-full"
                    >
                        {renderContent()}
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* 分隔线 */}
            <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-[11px] text-gray-400">其他登录方式</span>
                <div className="flex-1 h-px bg-gray-200" />
            </div>

            {/* 底部图标按钮 - 小巧整洁 */}
            <div className="flex justify-center gap-3">
                {getOtherMethods().map((method) => {
                    const Icon = method.icon;
                    return (
                        <motion.button
                            key={method.key}
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => setActiveMethod(method.key)}
                            className="flex flex-col items-center gap-1.5 px-5 py-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all"
                        >
                            <Icon className="w-5 h-5 text-gray-400" />
                            <span className="text-[11px] text-gray-500">{method.label}</span>
                        </motion.button>
                    );
                })}
            </div>
        </div>
    );
};

export default SelectMethodStep;
